/**
 * Result Modal Module
 * Displays success/error results after form submission
 * Separate from registration modal for clean UX flow
 */

// Module state
let currentType = null;

/**
 * Initialize the result modal
 */
export function initResultModal() {
  const modal = document.getElementById('result-modal');
  if (!modal) return;

  const closeButtons = modal.querySelectorAll('[data-result-modal-close]');
  const retryButton = document.getElementById('result-modal-retry');

  // Close modal handlers
  closeButtons.forEach(btn => {
    btn.addEventListener('click', () => {
      closeResultModal();
    });
  });

  // Close on Escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !modal.hidden) {
      closeResultModal();
    }
  });

  // Retry button - close result modal and reopen registration modal
  if (retryButton) {
    retryButton.addEventListener('click', () => {
      closeResultModal();
      // Dispatch custom event to reopen registration modal
      window.dispatchEvent(new CustomEvent('reopenRegistrationModal'));
    });
  }
}

/**
 * Show the result modal
 * @param {'success' | 'error'} type - The type of result to show
 * @param {string} [customMessage] - Optional custom message (for errors)
 */
export function showResultModal(type, customMessage = null) {
  const modal = document.getElementById('result-modal');
  const successState = document.getElementById('result-modal-success');
  const errorState = document.getElementById('result-modal-error');
  const errorMessage = document.getElementById('result-modal-error-message');

  if (!modal || !successState || !errorState) return;

  currentType = type;

  // Reset states
  successState.hidden = true;
  errorState.hidden = true;

  // Show appropriate state
  if (type === 'success') {
    successState.hidden = false;
    // Update aria-labelledby to success title
    modal.setAttribute('aria-labelledby', 'result-modal-title');
  } else {
    errorState.hidden = false;
    // Update aria-labelledby to error title
    modal.setAttribute('aria-labelledby', 'result-modal-title-error');
    // Set custom error message if provided
    if (customMessage && errorMessage) {
      errorMessage.textContent = customMessage;
    }
  }

  // Show modal
  modal.hidden = false;
  document.body.style.overflow = 'hidden';

  // Focus the appropriate element
  const focusTarget = type === 'success'
    ? modal.querySelector('.modal__result--success .btn')
    : modal.querySelector('.modal__result--error .btn');

  if (focusTarget) {
    setTimeout(() => focusTarget.focus(), 100);
  }

  // Trap focus inside modal
  trapFocus(modal);
}

/**
 * Close the result modal
 */
export function closeResultModal() {
  const modal = document.getElementById('result-modal');
  if (!modal) return;

  modal.hidden = true;
  document.body.style.overflow = '';
  currentType = null;
}

/**
 * Trap focus inside modal for accessibility
 * @param {HTMLElement} modal - The modal element
 */
function trapFocus(modal) {
  const focusableElements = modal.querySelectorAll(
    'button:not([disabled]), a[href]:not([disabled]), [tabindex]:not([tabindex="-1"])'
  );

  if (focusableElements.length === 0) return;

  const firstFocusable = focusableElements[0];
  const lastFocusable = focusableElements[focusableElements.length - 1];

  const handleTab = (e) => {
    if (e.key !== 'Tab') return;

    if (e.shiftKey) {
      if (document.activeElement === firstFocusable) {
        e.preventDefault();
        lastFocusable.focus();
      }
    } else {
      if (document.activeElement === lastFocusable) {
        e.preventDefault();
        firstFocusable.focus();
      }
    }
  };

  // Remove existing listener if any
  modal.removeEventListener('keydown', modal._trapFocusHandler);
  modal._trapFocusHandler = handleTab;
  modal.addEventListener('keydown', handleTab);
}
